/**
 * Chat message validation utility
 * Validates: Requirements 5.8, 5.9, 5.10
 */

import type { ValidationResult } from '../types';

/**
 * Maximum message length
 */
export const MAX_MESSAGE_LENGTH = 200;

/**
 * Validate chat message
 * @param message - Message text
 * @param userId - User ID
 * @param ngWords - Array of NG words
 * @param ngUsers - Array of NG user IDs
 * @returns Validation result
 */
export function validateMessage(
  message: string,
  userId: number,
  ngWords: string[],
  ngUsers: number[]
): ValidationResult {
  // Check if message is empty
  if (!message || message.trim().length === 0) {
    return {
      isValid: false,
      error: 'empty',
    };
  }

  // Check message length
  if (message.length > MAX_MESSAGE_LENGTH) {
    return {
      isValid: false,
      error: 'too_long',
    };
  }

  // Check if user is in NG users list
  if (ngUsers.includes(userId)) {
    return {
      isValid: false,
      error: 'ng_user',
    };
  }

  // Check if message contains NG words
  const lowerMessage = message.toLowerCase();
  for (const ngWord of ngWords) {
    if (lowerMessage.includes(ngWord.toLowerCase())) {
      return {
        isValid: false,
        error: 'ng_word',
      };
    }
  }

  return {
    isValid: true,
  };
}

/**
 * Check if chat is closed based on deadline
 * @param messageDeadline - ISO 8601 date string
 * @returns true if chat is closed
 */
export function isChatClosed(messageDeadline: string): boolean {
  const deadline = new Date(messageDeadline);
  const now = new Date();
  
  if (isNaN(deadline.getTime())) {
    return false;
  }

  return now.getTime() > deadline.getTime();
}

/**
 * Get validation error message
 * @param error - Validation error type
 * @returns User-friendly error message
 */
export function getValidationErrorMessage(
  error: 'ng_word' | 'ng_user' | 'too_long' | 'empty' | 'closed'
): string {
  switch (error) {
    case 'empty':
      return 'メッセージを入力してください';
    case 'too_long':
      return `メッセージは${MAX_MESSAGE_LENGTH}文字以内で入力してください`;
    case 'ng_word':
      return '禁止されている単語が含まれています';
    case 'ng_user':
      return 'メッセージを送信する権限がありません';
    case 'closed':
      return 'チャットの投稿は終了しました';
    default:
      return '不明なエラーが発生しました';
  }
}
